import { LockClosedIcon } from "@heroicons/react/24/outline";
import Badge from "components/Badge";
import { cn } from "utils/ui";
import { TempoChatListItemDto } from "./tempo-chat-list.typing";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

interface TempoChatListItemProps {
  chat: TempoChatListItemDto;
  unreadMessageCount?: number;
}
export const TempoChatListItem = ({ chat, unreadMessageCount = 0 }: TempoChatListItemProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging.tempo-chat-list-page",
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const openChat = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("chat", chat.id.toString());
    setSearchParams(newParams);
  };

  return (
    <div
      className={cn("flex cursor-pointer justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-700/30", {
        "bg-gray-100 text-gray-500 opacity-90 dark:bg-slate-700 dark:text-slate-500": chat.closed,
      })}
      onClick={openChat}
    >
      <div className="flex w-3/4 flex-1 flex-col">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Badge color="sky">{t("customer")}</Badge>
            <Badge>{chat.customerName}</Badge>
            <Badge>{chat.projectName}</Badge>
            <span className="font-medium">{chat.taskCode}</span>
            {chat.closed && (
              <div className="flex items-center gap-1 rounded-full border bg-gray-300 px-3 text-sm font-medium text-gray-700">
                <LockClosedIcon className="size-4" />
                {t("closed")}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="font-semibold">{chat.service}</span>
            <span className="text-color-secondary italic">
              {chat.sourceLanguage} → {chat.targetLanguage}
            </span>
          </div>
        </div>

        <div className="text-color-secondary mt-3 truncate text-sm italic">
          <LastMessageSent chat={chat} />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        {chat.lastMessageTime && (
          <span>{new Date(chat.lastMessageTime).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
        )}

        {unreadMessageCount > 0 && (
          <span className="h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-2 py-1 text-xs font-bold leading-none text-white">
            {unreadMessageCount > 9 ? "9+" : unreadMessageCount}
          </span>
        )}
      </div>
    </div>
  );
};

function LastMessageSent({ chat }: { chat: TempoChatListItemDto }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging",
  });
  if (chat.showLastMessagedAsDeleted) {
    return (
      <>
        <span className="font-semibold">{chat.lastMessageAuthorName}: </span>
        {t("moderation.deleteInfo")}
      </>
    );
  }
  if (!chat.lastMessageText) {
    return <>{t("tempo-chat-list-page.no-message")}</>;
  }
  return (
    <>
      <span className="font-semibold">{chat.lastMessageAuthorName}: </span>
      {chat.lastMessageText}
    </>
  );
}
